import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "../../services/api";

function Avatar({ prenom, nom, photo, size = 180 }) {
  const initiales = `${(prenom || "?")[0]}${(nom || "?")[0]}`.toUpperCase();
  const colors = ["#1565c0", "#2e7d32", "#6a1b9a", "#c62828", "#00695c"];
  const bg = colors[(nom?.charCodeAt(0) || 0) % colors.length];
  if (photo)
    return (
      <img src={`${photo}`} alt={`${prenom} ${nom}`}
        style={{
          width: `${size}px`, height: `${size}px`, borderRadius: "12px",
          objectFit: "cover", flexShrink: 0, border: `2px solid ${bg}33`,
        }}
      />
    );
  return (
    <MDBox sx={{
      width: `${size}px`, height: `${size}px`, borderRadius: "12px",
      background: bg, color: "#fff",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 700, fontSize: `${size * 0.35}px`, flexShrink: 0,
    }}>
      {initiales}
    </MDBox>
  );
}

function Enseignants() {
  const navigate = useNavigate();
  const [enseignants, setEnseignants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const isMobile = window.innerWidth < 768;
  const [viewMode, setViewMode] = useState(isMobile ? "cards" : "table");

  useEffect(() => { fetchEnseignants(); }, []);

  const fetchEnseignants = async () => {
    try {
      const { data } = await api.get("/enseignants");
      setEnseignants(data);
    } catch (error) {
      console.error("Erreur:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet enseignant ?")) return;
    try {
      await api.delete(`/enseignants/${id}`);
      fetchEnseignants();
    } catch (error) {
      alert(error.response?.data?.message || "Erreur lors de la suppression");
    }
  };

  const filtered = enseignants.filter((e) =>
    `${e.prenom} ${e.nom}`.toLowerCase().includes(search.toLowerCase()) ||
    e.email?.toLowerCase().includes(search.toLowerCase()) ||
    e.ville?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>

              {/* Header */}
              <MDBox mx={2} mt={-3} py={3} px={3} sx={{
                background: "linear-gradient(135deg, #6a1b9a 0%, #7b1fa2 100%)",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(106,27,154,0.4)",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                flexWrap: "wrap", gap: 2,
              }}>
                <MDBox>
                  <MDTypography variant="h5" color="white" fontWeight="bold">
                    Gestion des Enseignants
                  </MDTypography>
                  <MDTypography variant="caption" color="white" sx={{ opacity: 0.8 }}>
                    {enseignants.length} enseignant{enseignants.length > 1 ? "s" : ""} enregistré{enseignants.length > 1 ? "s" : ""}
                  </MDTypography>
                </MDBox>
                <MDButton variant="contained" size="medium"
                  onClick={() => navigate("/enseignants/nouveau")}
                  sx={{
                    background: "#fff", color: "#6a1b9a",
                    fontWeight: 700, borderRadius: "10px",
                    "&:hover": { background: "#f3e5f5" },
                  }}>
                  <Icon sx={{ mr: { xs: 0, sm: 0.5 } }}>add</Icon>
                  <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                    Ajouter un enseignant
                  </MDBox>
                </MDButton>
              </MDBox>

              <MDBox pt={3} px={3} pb={3}>
                <MDBox display="flex" justifyContent="space-between"
                  alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                  <MDBox sx={{ flex: 1, maxWidth: { xs: "100%", sm: 400 } }}>
                    <MDInput fullWidth
                      label="🔍  Rechercher par nom, email, ville..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                    />
                  </MDBox>
                  <MDBox display="flex" gap={1}>
                    <Tooltip title="Vue tableau">
                      <MDBox onClick={() => setViewMode("table")} sx={{
                        width: 36, height: 36, borderRadius: "8px", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: viewMode === "table" ? "#6a1b9a" : "#f5f5f5",
                        color: viewMode === "table" ? "#fff" : "#757575",
                        transition: "all 0.2s",
                      }}>
                        <Icon fontSize="small">table_rows</Icon>
                      </MDBox>
                    </Tooltip>
                    <Tooltip title="Vue cartes">
                      <MDBox onClick={() => setViewMode("cards")} sx={{
                        width: 36, height: 36, borderRadius: "8px", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: viewMode === "cards" ? "#6a1b9a" : "#f5f5f5",
                        color: viewMode === "cards" ? "#fff" : "#757575",
                        transition: "all 0.2s",
                      }}>
                        <Icon fontSize="small">grid_view</Icon>
                      </MDBox>
                    </Tooltip>
                  </MDBox>
                </MDBox>

                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={4}>
                    <MDTypography variant="body2" color="text">Chargement...</MDTypography>
                  </MDBox>
                ) : filtered.length === 0 ? (
                  <MDBox display="flex" flexDirection="column"
                    alignItems="center" justifyContent="center" py={6} gap={2}>
                    <Icon sx={{ fontSize: 60, color: "#e0e0e0" }}>person</Icon>
                    <MDTypography variant="h6" color="text" fontWeight="medium">
                      Aucun enseignant trouvé
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      {search ? "Essayez une autre recherche" : "Commencez par ajouter un enseignant"}
                    </MDTypography>
                  </MDBox>

                ) : viewMode === "table" ? (
                  <MDBox sx={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          {["Enseignant", "Ville", "Téléphone", "Email", "Cours", "Actions"].map((h) => (
                            <th key={h} style={{
                              padding: "10px 16px", textAlign: "left",
                              fontSize: 11, fontWeight: 700, color: "#9e9e9e",
                              textTransform: "uppercase", letterSpacing: "0.05em",
                              borderBottom: "2px solid #f5f5f5",
                            }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((e) => (
                          <tr key={e.id}
                            style={{ borderBottom: "1px solid #fafafa" }}
                            onMouseEnter={(ev) => (ev.currentTarget.style.background = "#fdf8ff")}
                            onMouseLeave={(ev) => (ev.currentTarget.style.background = "transparent")}
                          >
                            <td style={{ padding: "14px 16px" }}>
                              <MDBox display="flex" alignItems="center" gap={1.5}>
                                <Avatar prenom={e.prenom} nom={e.nom} photo={e.photo} size={56} />
                                <MDTypography variant="button" fontWeight="bold" sx={{ color: "#344767" }}>
                                  {e.prenom} {e.nom}
                                </MDTypography>
                              </MDBox>
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <MDTypography variant="caption" sx={{ color: "#7b809a" }}>
                                {e.ville || "—"}
                              </MDTypography>
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <MDTypography variant="caption" sx={{ color: "#7b809a" }}>
                                {e.telephone || "—"}
                              </MDTypography>
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <MDTypography variant="caption" sx={{ color: "#7b809a" }}>
                                {e.email || "—"}
                              </MDTypography>
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <MDBox sx={{
                                background: "#f3e5f5", color: "#6a1b9a",
                                borderRadius: "10px", padding: "2px 10px",
                                fontSize: 11, fontWeight: 600, display: "inline-block",
                              }}>
                                {e.cours?.length ?? 0} cours
                              </MDBox>
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <MDBox display="flex" gap={1}>
                                <Tooltip title="Voir la fiche">
                                  <MDBox onClick={() => navigate(`/enseignants/${e.id}`)} sx={{
                                    width: 32, height: 32, borderRadius: "8px",
                                    background: "#f3e5f5", color: "#6a1b9a",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    cursor: "pointer", "&:hover": { background: "#e1bee7" },
                                  }}>
                                    <Icon sx={{ fontSize: 16 }}>visibility</Icon>
                                  </MDBox>
                                </Tooltip>
                                <Tooltip title="Modifier">
                                  <MDBox onClick={() => navigate(`/enseignants/${e.id}/modifier`)} sx={{
                                    width: 32, height: 32, borderRadius: "8px",
                                    background: "#fff8e1", color: "#f57f17",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    cursor: "pointer", "&:hover": { background: "#ffecb3" },
                                  }}>
                                    <Icon sx={{ fontSize: 16 }}>edit</Icon>
                                  </MDBox>
                                </Tooltip>
                                <Tooltip title="Supprimer">
                                  <MDBox onClick={() => handleDelete(e.id)} sx={{
                                    width: 32, height: 32, borderRadius: "8px",
                                    background: "#ffebee", color: "#c62828",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    cursor: "pointer", "&:hover": { background: "#ffcdd2" },
                                  }}>
                                    <Icon sx={{ fontSize: 16 }}>cancel</Icon>
                                  </MDBox>
                                </Tooltip>
                              </MDBox>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </MDBox>

                ) : (
                  <Grid container spacing={2}>
                    {filtered.map((e) => (
                      <Grid item xs={12} sm={6} md={4} key={e.id}>
                        <MDBox sx={{
                          background: "#fff", borderRadius: "16px",
                          border: "1px solid #f0f0f0", padding: "20px",
                          transition: "all 0.2s", cursor: "pointer",
                          "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,0.1)", transform: "translateY(-2px)" },
                        }}
                          onClick={() => navigate(`/enseignants/${e.id}`)}>
                          <MDBox display="flex" alignItems="center" gap={2} mb={2}>
                            <Avatar prenom={e.prenom} nom={e.nom} photo={e.photo} size={72} />
                            <MDBox>
                              <MDTypography variant="button" fontWeight="bold" sx={{ color: "#344767" }}>
                                {e.prenom} {e.nom}
                              </MDTypography>
                              {e.ville && (
                                <MDTypography variant="caption" color="text" display="block">
                                  📍 {e.ville}
                                </MDTypography>
                              )}
                            </MDBox>
                          </MDBox>
                          {e.telephone && (
                            <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                              📞 {e.telephone}
                            </MDTypography>
                          )}
                          {e.email && (
                            <MDTypography variant="caption" color="text" display="block" mb={1}>
                              ✉️ {e.email}
                            </MDTypography>
                          )}
                          <MDBox sx={{
                            background: "#f3e5f5", color: "#6a1b9a",
                            borderRadius: "10px", padding: "2px 10px",
                            fontSize: 11, fontWeight: 600, display: "inline-block", mb: 2,
                          }}>
                            {e.cours?.length ?? 0} cours
                          </MDBox>
                          <MDBox display="flex" gap={1} mt={1} onClick={(ev) => ev.stopPropagation()}>
                            <MDBox onClick={() => navigate(`/enseignants/${e.id}/modifier`)} sx={{
                              flex: 1, padding: "6px", borderRadius: "8px",
                              background: "#fff8e1", color: "#f57f17",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              cursor: "pointer", fontSize: 12, fontWeight: 600, gap: 0.5,
                              "&:hover": { background: "#ffecb3" },
                            }}>
                              <Icon sx={{ fontSize: 14 }}>edit</Icon> Modifier
                            </MDBox>
                            <MDBox onClick={() => handleDelete(e.id)} sx={{
                              flex: 1, padding: "6px", borderRadius: "8px",
                              background: "#ffebee", color: "#c62828",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              cursor: "pointer", fontSize: 12, fontWeight: 600, gap: 0.5,
                              "&:hover": { background: "#ffcdd2" },
                            }}>
                              <Icon sx={{ fontSize: 14 }}>cancel</Icon> Supprimer
                            </MDBox>
                          </MDBox>
                        </MDBox>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Enseignants;